import * as React from "react";
import { TextField } from "@mui/material";
import { Theme, darken, lighten } from "@mui/material/styles";
import { NumericFormat } from "react-number-format";
import { FieldLabelWithHelp } from "./FieldHelpIcon";

type BaseInputNumberProps = {
  label: string;
  helperText?: string;
  error?: boolean;
  // Aviso que no bloquea: el helperText se pinta de advertencia. Un error lo tapa.
  warning?: boolean;
  allowNegative?: boolean;
  allowedDecimalSeparators?: string[];
  decimalSeparator?: string;
  prefix?: string;
  suffix?: string;
  thousandsGroupStyle?: "none" | "thousand" | "lakh" | "wan";
  thousandSeparator?: string;
  decimalScale?: number;
  fixedDecimalScale?: boolean;
  autoFocus?: boolean;
  customWidth?: number;
  disabled?: boolean;
  hidden?: boolean;
  placeholder?: string;
  nullText?: string;
  description?: string;
};

type NullableInputNumberProps = BaseInputNumberProps & {
  nullable: true;
  value: number | undefined;
  onChange: (value: number | undefined) => void;
};

type NonNullableInputNumberProps = BaseInputNumberProps & {
  nullable?: false;
  value: number;
  onChange: (value: number) => void;
};

type InputNumberProps = NullableInputNumberProps | NonNullableInputNumberProps;

export const InputNumber: React.FC<InputNumberProps> = ({
  label,
  value,
  onChange,
  helperText = "",
  error = false,
  warning = false,
  nullable = false,
  allowNegative = false,
  allowedDecimalSeparators = [".", ","],
  decimalSeparator = ".",
  prefix,
  suffix,
  thousandsGroupStyle = "none",
  thousandSeparator,
  decimalScale = 0,
  fixedDecimalScale = false,
  autoFocus = false,
  customWidth = 500,
  disabled = false,
  hidden = false,
  placeholder,
  nullText,
  description,
}) => {
  const [focused, setFocused] = React.useState(false);
  if (hidden) return null;
  const isEmpty =
    value === undefined ||
    value === null ||
    (typeof value === "string" && value === "");
  const showLabelInside = description && isEmpty && !focused;
  const baseLabel =
    nullText && isEmpty && !focused ? `${label} (${nullText})` : label;
  const displayLabel = showLabelInside ? undefined : baseLabel;
  const effectivePlaceholder = showLabelInside
    ? nullText
      ? `${label} (${nullText})`
      : label
    : placeholder;
  return (
    <NumericFormat
      disabled={disabled}
      autoFocus={autoFocus}
      size={"small"}
      value={value}
      customInput={TextField}
      onValueChange={(values, _sourceInfo) => {
        // Si es nullable y el campo está vacío o inválido
        if (nullable && values.floatValue === undefined) {
          (onChange as (value: number | undefined) => void)(undefined);
        }
        // Si hay un valor numérico válido
        else if (values.floatValue !== undefined) {
          onChange(values.floatValue as any);
        }
        // Para campos no nullable con valor vacío, no hacemos nada
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      allowNegative={allowNegative}
      allowedDecimalSeparators={allowedDecimalSeparators}
      decimalSeparator={decimalSeparator}
      prefix={prefix}
      suffix={suffix}
      decimalScale={decimalScale}
      fixedDecimalScale={fixedDecimalScale}
      thousandSeparator={thousandSeparator}
      thousandsGroupStyle={thousandsGroupStyle}
      error={error}
      helperText={helperText}
      FormHelperTextProps={
        warning && !error
          ? {
              sx: {
                color: (theme: Theme) =>
                  theme.palette.mode === "dark"
                    ? lighten(theme.palette.warning.main, 0.5)
                    : darken(theme.palette.warning.main, 0.5),
              },
            }
          : undefined
      }
      sx={{ m: 1, mb: 1.5, width: customWidth }}
      label={
        <FieldLabelWithHelp label={displayLabel} description={description} />
      }
      InputLabelProps={description ? { shrink: true } : undefined}
      placeholder={effectivePlaceholder}
    />
  );
};
